/* ApplyRemote — public application landing (bilingual, friendly).
 *
 * Path:  /assessment/visa-operations
 *
 * Shorter, warmer, and bilingual. Single phone field with a "WhatsApp =
 * same number" checkbox. No legal/contract phrasing. Softer palette
 * (warm cream + muted gold + teal accents).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, Compass, ShieldCheck, Monitor } from "lucide-react";
import IntroExplainer from "./IntroExplainer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplyRemote() {
  const navigate = useNavigate();
  const [intakeOpen, setIntakeOpen] = useState(true);
  const [intakeChecked, setIntakeChecked] = useState(false);
  useEffect(() => {
    document.title = "Apply — Visa Operations / التقديم";
    axios.get(`${API}/ats/public/intake-status`)
      .then(({ data }) => { setIntakeOpen(!!data.active); setIntakeChecked(true); })
      .catch(() => setIntakeChecked(true));
  }, []);

  const [form, setForm] = useState({
    full_name: "", country: "السعودية", city: "", mobile: "",
    whatsapp_same: true, whatsapp: "",
    email: "", nationality: "",
    english_level: "good", has_visa_experience: false, years_experience: 0,
    daily_hours_available: 4,
    accepts_per_transaction_pay: true, privacy_accepted: false,
    test_acknowledged: false,
    referral_source: "",  // facebook | linkedin | whatsapp | other | ""
  });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setErr("");
    if (form.full_name.trim().length < 3) {
      setErr("الاسم الكامل مطلوب • Full name is required"); return;
    }
    if (!form.mobile.trim()) {
      setErr("رقم الجوال مطلوب • Mobile number is required"); return;
    }
    if (!cv) {
      setErr("السيرة الذاتية مطلوبة • Resume is required"); return;
    }
    const okExt = /\.(pdf|doc|docx|txt)$/i.test(cv.name);
    if (!okExt) {
      setErr("صيغة الملف غير مدعومة. PDF أو DOCX فقط • Unsupported file. PDF or DOCX only"); return;
    }
    if (cv.size > 5 * 1024 * 1024) {
      setErr("حجم الملف أكبر من 5MB • File exceeds 5MB"); return;
    }
    if (!form.privacy_accepted) {
      setErr("يرجى الموافقة على الخصوصية • Please accept the privacy notice"); return;
    }
    if (!form.test_acknowledged) {
      setErr("يجب التعهّد باجتياز الاختبار العملي قبل المتابعة • You must acknowledge the assessment requirement"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        whatsapp: form.whatsapp_same ? form.mobile : (form.whatsapp || form.mobile),
        arabic_level: "native",  // legacy field kept for backend compatibility
        centers_dealt_with: [],
        previous_companies: "",
      };
      const fd = new FormData();
      fd.append("payload_json", JSON.stringify(payload));
      if (cv) fd.append("cv", cv);
      const { data } = await axios.post(`${API}/ats/apply`, fd);
      navigate(`/apply/test/${data.test_session_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر إرسال الطلب • Submission failed");
    } finally {
      setBusy(false);
    }
  };

  // ── Palette: warm cream + soft teal + muted gold, gentle on the eye.
  const c = {
    bg:      "#f6f1e7",
    card:    "#ffffff",
    border:  "#e9dfc6",
    primary: "#0d7c79",   // muted teal
    accent:  "#b8893a",   // warm gold (not harsh)
    text:    "#2b2a26",
    sub:     "#6b6a5e",
    soft:    "#fbf6ec",
    danger:  "#b35353",
  };
  const wrap = {
    minHeight: "100vh", background: `linear-gradient(180deg, ${c.bg} 0%, ${c.soft} 100%)`,
    color: c.text, padding: "32px 16px",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif",
  };
  const card = {
    maxWidth: 620, margin: "0 auto", background: c.card,
    border: `1px solid ${c.border}`, borderRadius: 20, padding: 28,
    boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
  };
  const head = { textAlign: "center", marginBottom: 18 };
  const h1 = { fontSize: 24, fontWeight: 800, color: c.primary, marginBottom: 4 };
  const h1Ar = { fontSize: 20, fontWeight: 800, color: c.primary, direction: "rtl", marginBottom: 12 };
  const sub = { fontSize: 13, color: c.sub, lineHeight: 1.7 };
  const subAr = { fontSize: 13, color: c.sub, direction: "rtl", lineHeight: 1.9 };
  const bullet = { fontSize: 13, color: c.text, padding: "5px 0", display: "flex", alignItems: "flex-start", gap: 8 };
  const sectionTitle = { fontSize: 14, fontWeight: 800, color: c.accent, marginTop: 22, marginBottom: 10, letterSpacing: 0.3 };
  const label = { display: "block", fontSize: 12, fontWeight: 700, marginBottom: 6, color: c.text };
  const input = { width: "100%", padding: "10px 12px", background: "#fff", border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box" };
  const row = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10, marginBottom: 10 };

  return (
    <div style={wrap} data-testid="apply-remote-page">
      <div style={card}>
        {intakeChecked && !intakeOpen && (
          <div data-testid="intake-closed-notice"
               style={{ padding: 18, background: "#fff5f5", border: `1px solid ${c.danger}`, borderRadius: 12, marginBottom: 18, textAlign: "center" }}>
            <h3 style={{ ...h1, color: c.danger, fontSize: 17 }}>Applications are currently closed</h3>
            <p style={{ ...sub, marginTop: 6 }} dir="rtl">التقديم مغلق حالياً. نشكر اهتمامك، يمكنك العودة لاحقاً عند فتح باب التقديم من جديد.</p>
          </div>
        )}

        {/* ── Header ─────────────────────────────────────── */}
        <div style={head}>
          <h1 style={h1}>Remote Visa Operations Associate</h1>
          <h2 style={h1Ar}>متعاون عن بُعد لإدخال بيانات التأشيرات</h2>
          <p style={sub}>This is a remote, task-based opportunity. Payment is made per approved completed case, based on task type and complexity.</p>
          <p style={subAr}>العمل عن بُعد، والمقابل على كل معاملة منجزة ومعتمدة حسب نوع وصعوبة الحالة.</p>
        </div>

        {/* ── Desktop Mode notice — unified branding ─── */}
        <div data-testid="desktop-mode-notice" style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "#fff7e6", border: "1px solid #f0c040",
          borderInlineStart: "4px solid #d97706",
          borderRadius: 10, padding: "10px 14px", marginBottom: 14,
        }}>
          <Monitor size={22} color="#b45309" strokeWidth={2.2} style={{ flexShrink: 0 }}/>
          <div style={{ fontSize: 13, color: "#7c2d12", lineHeight: 1.7 }}>
            <b>Desktop Mode required</b> ـ يُفضّل بشدّة استخدام <b>كمبيوتر أو لابتوب</b> لإكمال التقييم بسهولة. الشاشات الصغيرة قد تُصعّب الإدخال.
          </div>
        </div>

        {/* ── Motion-graphics intro video with bilingual captions ── */}
        <IntroExplainer />

        {/* ── How the application works — soft, professional intro ─── */}
        <div data-testid="apply-test-warning"
             style={{
               background: "linear-gradient(180deg, #ffffff, #faf7f1)",
               border: `1px solid ${c.border}`,
               borderInlineStart: `4px solid ${c.primary}`,
               borderRadius: 12, padding: "16px 18px", marginBottom: 18, marginTop: 4,
               display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "start",
             }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10,
            background: `${c.primary}15`, color: c.primary,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BadgeCheck size={24} strokeWidth={2.2} />
          </div>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: c.text, marginBottom: 4 }} dir="rtl">
              خطوة قصيرة قبل التقديم
            </div>
            <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.9 }} dir="rtl">
              لا تُستلَم السير الذاتية مباشرة. نبدأ بـ <b style={{ color: c.text }}>اختبار أهلية قصير</b> تليه
              <b style={{ color: c.text }}> تجربة افتراضية بسيطة</b> تتعرّف فيها على طبيعة العمل قبل التقديم.<br/>
              يستلم الفريق المختص ملفك بعد اجتياز المرحلتين ـ هذا الدور يتطلّب إجادة الإنجليزية، والقدرة على التعامل مع مراكز التأشيرات بدقة.<br/>
              <span style={{ color: c.primary, fontWeight: 700 }}>والله وليّ التوفيق.</span>
            </div>
            <div style={{ fontSize: 11.5, color: c.sub, marginTop: 8, lineHeight: 1.6, fontStyle: "italic", opacity: 0.85 }}>
              We start every application with a short eligibility check and a brief sandbox walkthrough.
              Your file reaches the team once both steps are complete.
            </div>
          </div>
        </div>

        {/* ── About you ───────────────────────────────── */}
        <div style={sectionTitle}>About you · عن نفسك</div>
        <div style={row}>
          <div>
            <label style={label}>Full name · الاسم الكامل *</label>
            <input style={input} value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} data-testid="apply-name" />
          </div>
          <div>
            <label style={label}>Mobile · رقم الجوال *</label>
            <input style={input} dir="ltr" value={form.mobile} onChange={(e) => upd("mobile", e.target.value)} placeholder="+966 5XX XXX XXX" data-testid="apply-mobile" />
          </div>
          <div>
            <label style={label}>Country · الدولة</label>
            <input style={input} value={form.country} onChange={(e) => upd("country", e.target.value)} data-testid="apply-country" />
          </div>
          <div>
            <label style={label}>Email · البريد (اختياري)</label>
            <input style={input} dir="ltr" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="apply-email" />
          </div>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, cursor: "pointer", fontSize: 12.5, color: c.sub }}>
          <input type="checkbox" checked={form.whatsapp_same} onChange={(e) => upd("whatsapp_same", e.target.checked)} data-testid="apply-wa-same" />
          <span>WhatsApp same as mobile · واتساب نفس رقم الجوال</span>
        </label>
        {!form.whatsapp_same && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>WhatsApp · رقم واتساب</label>
            <input style={input} dir="ltr" value={form.whatsapp} onChange={(e) => upd("whatsapp", e.target.value)} placeholder="+966 5XX XXX XXX" data-testid="apply-whatsapp" />
          </div>
        )}

        {/* ── Quick experience — only what we actually need ── */}
        <div style={sectionTitle}>A bit about your background · معلومات سريعة</div>
        <div style={row}>
          <div>
            <label style={label}>English level · مستوى الإنجليزية</label>
            <select style={input} value={form.english_level} onChange={(e) => upd("english_level", e.target.value)} data-testid="apply-english">
              <option value="fluent">Fluent · طلق</option>
              <option value="good">Good · جيد</option>
              <option value="basic">Basic · أساسي</option>
            </select>
          </div>
          <div>
            <label style={label}>Worked with visas before? · هل سبق وعملت بالتأشيرات؟</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ k: true, l: "Yes · نعم" }, { k: false, l: "No · لا" }].map((o) => (
                <button key={String(o.k)} type="button" onClick={() => upd("has_visa_experience", o.k)}
                  style={{ flex: 1, padding: "9px 8px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700,
                           border: `1.5px solid ${form.has_visa_experience === o.k ? c.primary : c.border}`,
                           background: form.has_visa_experience === o.k ? c.primary : "#fff",
                           color: form.has_visa_experience === o.k ? "#fff" : c.text }}
                  data-testid={`apply-visa-exp-${o.k}`}>{o.l}</button>
              ))}
            </div>
          </div>
          {form.has_visa_experience && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={label}>Years of experience · كم سنة خبرة؟</label>
              <input style={input} type="number" min="0" max="40" value={form.years_experience}
                     onChange={(e) => upd("years_experience", +e.target.value)} data-testid="apply-years" />
            </div>
          )}
        </div>

        {/* ── Referral source (optional, single line) ── */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...label, color: c.sub }}>How did you hear about us? · كيف وصلك الإعلان؟ (اختياري)</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {[
              { k: "facebook", l: "Facebook · فيسبوك" },
              { k: "linkedin", l: "LinkedIn · لينكدإن" },
              { k: "whatsapp", l: "WhatsApp · واتساب" },
              { k: "other",    l: "Other · أخرى" },
            ].map((o) => {
              const active = form.referral_source === o.k;
              return (
                <button key={o.k} type="button"
                  onClick={() => upd("referral_source", active ? "" : o.k)}
                  data-testid={`apply-referral-${o.k}`}
                  style={{
                    padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontSize: 11.5, fontWeight: 600,
                    border: `1px solid ${active ? c.primary : c.border}`,
                    background: active ? `${c.primary}` : "#fff",
                    color: active ? "#fff" : c.sub,
                    textAlign: "center",
                  }}>
                  {o.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── CV ─────────────────────────────────────── */}
        <div style={sectionTitle}>
          Resume · السيرة الذاتية (PDF / DOCX) <span style={{ color: c.danger }}>*</span>
        </div>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={(e) => setCv(e.target.files?.[0] || null)}
          data-testid="apply-cv"
          required
          style={{ marginBottom: 8, color: c.sub, fontSize: 13 }}
        />
        {cv
          ? <div style={{ fontSize: 12, color: c.primary, marginBottom: 8 }}>✓ {cv.name} · {(cv.size / 1024).toFixed(0)} KB</div>
          : <div style={{ fontSize: 11.5, color: c.sub, marginBottom: 8, lineHeight: 1.7 }}>
              Required — PDF preferred, DOCX accepted (max 5MB).<br/>
              <span dir="rtl">إرفاق السيرة إلزامي — يُفضّل PDF، ويُقبل DOCX (الحد الأقصى 5 ميغابايت).</span>
            </div>
        }

        {/* ── Acknowledgement ─────────────────────────── */}
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "16px 0 12px", cursor: "pointer", padding: 12, background: c.soft, border: `1px solid ${c.border}`, borderRadius: 10 }}>
          <input type="checkbox" checked={form.privacy_accepted} onChange={(e) => upd("privacy_accepted", e.target.checked)} data-testid="apply-privacy" style={{ marginTop: 3 }} />
          <span style={{ fontSize: 12.5, lineHeight: 1.8, color: c.text }}>
            I consent to processing of my information for recruitment purposes only.<br />
            <span dir="rtl">أوافق على معالجة بياناتي وسيرتي الذاتية لغرض التقييم الوظيفي فقط. تُحفظ بياناتي بأمان ولا تُشارك مع أي طرف ثالث.</span>
          </span>
        </label>

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "0 0 18px", cursor: "pointer", padding: 12, background: `${c.primary}0a`, border: `1px solid ${c.primary}40`, borderRadius: 10 }}>
          <input type="checkbox" checked={form.test_acknowledged} onChange={(e) => upd("test_acknowledged", e.target.checked)} data-testid="apply-test-ack" style={{ marginTop: 3 }} />
          <span style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.85, color: c.text }} dir="rtl">
            <ShieldCheck size={20} style={{ color: c.primary, flexShrink: 0, marginTop: 1 }} strokeWidth={2.2} />
            <span>
              أعطي الإذن لمعالجة بياناتي ضمن إجراءات التوظيف، وأفهم أن ملفي يصل للفريق المختص <b style={{ color: c.primary }}>بعد إكمال اختبار الأهلية والتجربة الافتراضية</b>.
            </span>
          </span>
        </label>

        {err && (
          <div style={{ padding: 10, marginBottom: 12, background: "#fdf2f2", border: `1px solid ${c.danger}`, borderRadius: 10, color: c.danger, fontSize: 13, textAlign: "center" }} data-testid="apply-error">{err}</div>
        )}

        <button type="button" onClick={submit} disabled={busy || !intakeOpen}
          style={{ width: "100%", padding: "14px",
                   background: !intakeOpen ? "#9aa0a6" : c.primary,
                   color: "#fff", border: "none", borderRadius: 12,
                   fontWeight: 800, fontSize: 15,
                   cursor: (busy || !intakeOpen) ? "not-allowed" : "pointer",
                   opacity: (busy || !intakeOpen) ? 0.7 : 1,
                   boxShadow: !intakeOpen ? "none" : "0 4px 14px rgba(13,124,121,0.25)" }}
          data-testid="apply-submit">
          {!intakeOpen ? "Applications closed · التقديم مغلق"
            : busy ? "Submitting… · جارٍ الإرسال…"
            : "Continue to assessment · إرسال والمتابعة للاختبار"}
        </button>

        <p style={{ ...sub, textAlign: "center", marginTop: 14, fontSize: 11.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
          <Compass size={14} style={{ color: c.primary }} />
          <span dir="rtl">ستنتقل بعد الإرسال إلى اختبار الأهلية ثم التجربة الافتراضية ـ خطوات بسيطة لا تستغرق أكثر من بضع دقائق.</span>
        </p>
      </div>
    </div>
  );
}
